import {Injectable} from '@angular/core';
import {APIService} from './api.service';

export type CrmActivityType = 'note' | 'call' | 'email' | 'meeting' | 'cover_letter' | 'status_change' | 'system';

/** Activity types a user may log via the form; system + status_change are emitted by the backend only. */
export type UserLoggableActivityType = 'note' | 'call' | 'email' | 'meeting' | 'cover_letter';

export interface Contact {
  id: number;
  OrganizationTenant_id: number;
  name: string;
  role?: string | null;
  email?: string | null;
  phone?: string | null;
  is_primary: boolean;
  notes?: string | null;
  created_at?: string;
  updated_at?: string;
}

/**
 * A contact plus the counterparty it belongs to, for a deal's picker.
 *
 * `company` is client-side only: the API returns plain Contact rows, and this
 * is set from which counterparty the row was fetched for. It stays undefined
 * while a list holds one company's contacts, since naming the same company on
 * every option is noise. See listDealContacts().
 */
export interface DealContact extends Contact {
  company?: string | null;
}

export interface CrmActivity {
  id: number;
  OrganizationTenant_id: number;
  Contact_id?: number | null;
  User_id?: number | null;
  type: CrmActivityType;
  body: string;
  occurred_at: string;
  contact?: Contact | null;
  user?: {id: number; name_first?: string; name_last?: string} | null;
  created_at?: string;
}

export interface OrgOpportunity {
  id: number;
  title: string;
  status: string;
  rate_target?: number | null;
  submission_deadline?: string | null;
  won_at?: string | null;
  owner?: {id: number; name_first?: string; name_last?: string} | null;
  created_at?: string;
  for_Client_id?: number | null;
  via_Client_id?: number | null;
  /** Which slot this org filled on the deal, computed per requested Client. */
  org_role?: 'end_client' | 'broker' | 'both';
  submissions?: {id: number; Opportunity_id: number; status: string; consultant_User_id: number}[];
}

const logEmpty = (e: unknown): Contact[] => { console.error('contacts load failed', e); return []; };

/**
 * Per-tenant CRM API for a given OrganizationTenant ("account"): contacts CRUD
 * and the activity timeline. All endpoints are nested under the OT id, which the
 * backend resolves tenant-scoped (a cross-tenant id is a 404).
 */
@Injectable({providedIn: 'root'})
export class OrganizationCrmService extends APIService {

  public async listContacts(clientId: number): Promise<Contact[]> {
    const res = await this.get<{data: Contact[]}>(`/clients/${clientId}/contacts`, 'Could not load contacts');
    return res?.data ?? [];
  }

  /**
   * Contacts for both of a deal's counterparties, in one list.
   *
   * A deal is for an end client and may be routed through a broker, and the
   * person you actually deal with is often at the broker. The pickers loaded
   * the end client's contacts alone, so a broker contact could not be named
   * even after the API was widened to accept one (#1194).
   *
   * The two sides are fetched in parallel and concatenated end client first,
   * which leaves the common brokerless deal ordered exactly as before. Each
   * side is independently non-fatal: a side that fails (already toasted)
   * degrades to [], so one unreachable side leaves the other's contacts
   * rather than emptying the picker.
   *
   * No broker contact in the list means every option belongs to the end client
   * and none needs a label, which is the large majority of deals and leaves
   * them rendering exactly as before. Once one is in the list, every option is
   * labelled including the end client's own, because labelling the newcomers
   * alone would leave the rest reading as "company unstated".
   */
  public async listDealContacts(
    end: {id?: number | null; name?: string | null} | null | undefined,
    via: {id?: number | null; name?: string | null} | null | undefined,
  ): Promise<DealContact[]> {
    const [endRows, viaRows] = await Promise.all([
      end?.id ? this.listContacts(end.id).catch(logEmpty) : Promise.resolve([]),
      via?.id ? this.listContacts(via.id).catch(logEmpty) : Promise.resolve([]),
    ]);
    if (!viaRows.length) { return endRows; }
    return [
      ...endRows.map(c => ({...c, company: end?.name ?? null})),
      ...viaRows.map(c => ({...c, company: via?.name ?? null})),
    ];
  }

  public async createContact(clientId: number, payload: Partial<Contact>): Promise<Contact | null> {
    // Callers re-sync from the server via loadCrm(), so a null here is harmless.
    const res = await this.post<Partial<Contact>, {data: Contact}>(`/clients/${clientId}/contacts`, payload);
    return res?.data ?? null;
  }

  public async updateContact(clientId: number, id: number, payload: Partial<Contact>): Promise<Contact | null> {
    const res = await this.put<Partial<Contact>, {data: Contact}>(`/contacts/${id}`, payload);
    return res?.data ?? null;
  }

  public async deleteContact(clientId: number, id: number): Promise<void> {
    await this.delete(`/contacts/${id}`);
  }

  public async listActivities(clientId: number): Promise<CrmActivity[]> {
    const res = await this.get<{data: CrmActivity[]}>(`/clients/${clientId}/activities`, 'Could not load activity timeline');
    return res?.data ?? [];
  }

  public async logActivity(
    clientId: number,
    payload: {type: UserLoggableActivityType; body: string; Contact_id?: number | null; occurred_at?: string},
  ): Promise<CrmActivity | null> {
    const res = await this.post<typeof payload, {data: CrmActivity}>(`/clients/${clientId}/activities`, payload);
    return res?.data ?? null;
  }

  public async updateActivity(
    id: number,
    payload: {body: string; Contact_id?: number | null; occurred_at?: string},
  ): Promise<CrmActivity | null> {
    const res = await this.put<typeof payload, {data: CrmActivity}>(`/crm-activities/${id}`, payload);
    return res?.data ?? null;
  }

  /**
   * Rejects on a 403/404 (toasted by errorify); on 2xx the body says whether
   * the delete actually happened, so callers don't optimistically drop a row
   * the server declined to remove.
   */
  public async deleteActivity(id: number): Promise<boolean> {
    const res = await this.delete<{data: boolean}>(`/crm-activities/${id}`);
    return !!res?.data;
  }

  public async listOpportunities(clientId: number): Promise<OrgOpportunity[]> {
    const res = await this.get<{data: OrgOpportunity[]}>(`/clients/${clientId}/opportunities`, 'Could not load opportunity history');
    return res?.data ?? [];
  }
}
